import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendListaCasosArchivado,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import { formatearFecha } from "../../../components/common/helper/formatoFecha";
import mock_Json from "../../../dataJson/casosArchivados.json";
import {
  formatDescripcion,
  formatearMensajeBackend,
  ordenarCasosUsuarioBackend,
  procesarPropiedadMapeo,
  shouldRenderTicket,
} from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  listaCasosArchivado: any[];
  status: TypeStatus;
  processDefinitionIdDisplayName: string;
  processDefinitionIdVersion: string;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  listaCasosArchivado: [],
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
  processDefinitionIdDisplayName: "",
  processDefinitionIdVersion: "",
};
interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const listaCasosArchivadoSlice = createSlice({
  name: "listaCasosArchivado",
  initialState,
  reducers: {
    setListaCasosArchivado: (
      state,
      action: PayloadAction<{ casos: any[]; numPagina: number }>,
    ) => {
      const { casos, numPagina } = action.payload;

      // Si la página es 1, reemplazamos los datos, si no, concatenamos con los existentes
      if (numPagina === 0) {
        state.listaCasosArchivado = casos;
      } else {
        state.listaCasosArchivado = [...state.listaCasosArchivado, ...casos];
      }
    },
    getListaCasosArchivado: (state, action: PayloadAction<any[]>) => {
      state.listaCasosArchivado = action.payload;
    },
    setClearListaCasosArchivado: (state, action: PayloadAction<any[]>) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setProcessDefinitionIdDisplayName: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.processDefinitionIdDisplayName = action.payload;
    },
    setProcessDefinitionIdVersion: (state, action: PayloadAction<string>) => {
      state.processDefinitionIdVersion = action.payload;
    },
  },
});

const procesarCasosArchivados = async (casos: any[]) => {
  return await Promise.all(
    casos.map(async (caso) => {
      const {
        Prioridad,
        alarma,
        descripcion,
        ci,
        start,
        end_date,
        Usuario,
        NombreContrato,
        ticketRedmine,
      } = caso;
      let partesResumen = [];
      if (alarma) {
        partesResumen.push(`Titulo: ${alarma}`);
      }
      if (descripcion) {
        const value = formatDescripcion(descripcion);
        partesResumen.push(`Descripción: ${value}`);
      }
      if (ci) {
        partesResumen.push(`Ci: ${ci}`);
      }
      if (Prioridad) {
        partesResumen.push(`Prioridad: ${Prioridad}`);
      }
      if (Usuario) {
        partesResumen.push(`Usuario: ${Usuario}`);
      }
      if (NombreContrato) {
        partesResumen.push(`Contrato: ${NombreContrato}`);
      }

      const renderTicketRedmine = shouldRenderTicket(ticketRedmine);

      if (renderTicketRedmine) {
        partesResumen.push(`Ticket Redmine Nro: ${ticketRedmine}`);
      }
      const resumen =
        partesResumen.length > 0
          ? partesResumen.join("\n\n")
          : "No pudimos obtener los datos del caso";

      return {
        ...caso,
        resumen,
        start: formatearFecha(start),
        end_date: formatearFecha(end_date),
      };
    }),
  );
};

export const setBackendListaCasosArchivado =
  (
    Cantidad: number,
    NumPagina: number,
    options?: { signal?: AbortSignal },
  ): Thunk =>
    async (dispatch): Promise<AxiosResponse | AxiosError> => {
      dispatch(setIsLoading(true));

      try {
        const response: AxiosResponse = await BackendListaCasosArchivado(
          Cantidad,
          NumPagina,
        );

        const isError =
          response.data?.Success === false ||
          (response.data?.ResultadoSolicitud !== undefined &&
            response.data?.ResultadoSolicitud !== 200);

        if (isError) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          let ordenarLista = [...(response.data.Data || [])];
          ordenarLista.sort(ordenarCasosUsuarioBackend);
          ordenarLista = procesarPropiedadMapeo(ordenarLista, "prioridad");
          const listaFormateada = await procesarCasosArchivados(ordenarLista);

          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));

          dispatch(
            setListaCasosArchivado({
              casos: listaFormateada,
              numPagina: NumPagina,
            }),
          );
          if (!response.data) {
            response.data = { Data: [] };
          }
          response.data.Data = listaFormateada;
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

        return response.data;
      } catch (error: any) {
        dispatch(setIsLoading(false));
        dispatch(setSuccess(false));
        dispatch(setStatus("failed"));

        if (error.response?.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        } else if (error.response) {
          // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
          console.error(
            "Error de servidor:",
            error.response.status,
            error.response.data,
          );

          const errorData = error.response.data;
          const mensajeBackend = errorData?.Mensaje;
          const resultadoSolicitud = errorData?.ResultadoSolicitud;

          if (
            mensajeBackend &&
            (resultadoSolicitud !== 200 || errorData?.Success === false)
          ) {
            dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
          } else {
            dispatch(
              setMensaje(
                `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
              ),
            );
          }
        } else if (error.request) {
          console.error("Error de conexión:", error.message);
          dispatch(
            setMensaje(
              "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
            ),
          );
        } else {
          console.error("Error de red o solicitud:", error.message);
          dispatch(
            setMensaje(
              "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
            ),
          );
        }
        return error as AxiosError;
      } finally {
        dispatch(setIsLoading(false));
        if (getCurrentDomain() === "localhost") {
          dispatch(setIsLoading(true));
          let response: AxiosResponse = defaultResp;
          response.data = mock_Json;

          const mockData: any = mock_Json;
          const isErrorMock =
            mockData?.Success === false ||
            (mockData?.ResultadoSolicitud !== undefined &&
              mockData?.ResultadoSolicitud !== 200);

          if (isErrorMock) {
            dispatch(setIsLoading(false));
            dispatch(setStatus("failed"));
            dispatch(setSuccess(false));
          } else {
            let ordenarLista = [...(mock_Json.Data || [])];

            ordenarLista.sort(ordenarCasosUsuarioBackend);
            ordenarLista = procesarPropiedadMapeo(ordenarLista, "prioridad");
            const listaFormateada =
              await procesarCasosArchivados(ordenarLista);
            dispatch(
              setListaCasosArchivado({
                casos: listaFormateada,
                numPagina: NumPagina,
              }),
            );
            dispatch(setIsLoading(false));
            dispatch(setStatus("succeeded"));
            dispatch(setSuccess(true));
            if (!response.data) {
              response.data = { Data: [] };
            }
            response.data.Data = listaFormateada;
          }
          dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

          const mock401 = await import("../../../dataJson/401.json");
          if (mock401.status === 401) {
            dispatch(setStatus("noAutorice"));
            dispatch(
              setMensaje(
                formatearMensajeBackend(
                  "Tu sesión ha expirado. Vuelve a iniciar sesión",
                ),
              ),
            );
          }
        }
      }
    };

export const setBackendClearListaCasosArchivado =
  (): Thunk =>
    async (dispatch): Promise<AxiosResponse | AxiosError> => {
      dispatch(setIsLoading(true));
      try {
        let response: AxiosResponse = defaultResp;
        dispatch(
          setListaCasosArchivado({
            casos: [],
            numPagina: 0,
          }),
        );
        dispatch(setIsLoading(false));
        dispatch(setStatus("idle"));
        dispatch(setSuccess(false));
        dispatch(setMensaje(""));
        return response.data;
      } catch (error: any) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        return error as AxiosError;
      } finally {
        dispatch(setIsLoading(false));
      }
    };

export const {
  setListaCasosArchivado,
  getListaCasosArchivado,
  setProcessDefinitionIdDisplayName,
  setProcessDefinitionIdVersion,
  setClearListaCasosArchivado,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
  setError,
} = listaCasosArchivadoSlice.actions;

export default listaCasosArchivadoSlice.reducer;
